import React from 'react';

interface SettingsSearchState {
  query: string;
  setQuery: (query: string) => void;
  /** The keyboard-highlighted option, an index into the flattened results. */
  activeIndex: number;
  setActiveIndex: (index: number) => void;
}

const SettingsSearchContext = React.createContext<SettingsSearchState>({
  query: '',
  setQuery: () => undefined,
  activeIndex: 0,
  setActiveIndex: () => undefined,
});

/**
 * Shares the query between the field in the sidebar header and the results
 * that replace the sidebar's sections, which render in separate subtrees.
 */
export const SettingsSearchProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [query, setQueryState] = React.useState('');
  const [activeIndex, setActiveIndex] = React.useState(0);

  const value = React.useMemo(
    () => ({
      query,
      // A new query starts the highlight back at the first result.
      setQuery: (next: string) => {
        setQueryState(next);
        setActiveIndex(0);
      },
      activeIndex,
      setActiveIndex,
    }),
    [query, activeIndex],
  );

  return (
    <SettingsSearchContext.Provider value={value}>
      {children}
    </SettingsSearchContext.Provider>
  );
};

export const useSettingsSearchState = () =>
  React.useContext(SettingsSearchContext);
